import i18n from '../../i18n/config';
// frontend/src/components/schematics/VehicleTicketsDrawer.jsx
// Sidebar derecho con tickets del vehículo. Muestra fecha, tiempo transcurrido,
// perfil de alarma, número APS-, y botones rápidos Visto/Resolver que abren un
// popup con el detalle del ticket antes de confirmar.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Button,
    useTheme,
    useMediaQuery,
    Stack,
    Alert,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Assignment as TicketIcon,
    OpenInNew as OpenInNewIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Tune as ProfileIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';
import TicketQuickActions from '../tickets/TicketQuickActions';

// Maps de labels — los mismos que usa AlarmTicketDetailPage para mostrar texto humano.
const SEVERITY_LABELS = {
    alarm: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_atencion'),
    critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_critica'),
    extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_extrema'),
};
const ALARM_REASON_LABELS = {
    pressure_threshold: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_fuera_de_regla'),
    temperature_threshold: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_fuera_de_regla'),
    dual_yoyo: i18n.t('hardcoded.components_alarmprofiles_profileform.dual'),
    thermal_risk: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.riesgo_termico'),
    sensor_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte'),
    receiver_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte'),
};
const RULE_CODE_LABELS = {
    pressure_low_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja'),
    pressure_high_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta'),
    pressure_low_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja_critica'),
    pressure_high_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta_critica'),
    pressure_low_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja_extrema'),
    pressure_high_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta_extrema'),
    temperature_high_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_alta'),
    temperature_high_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_alta_critica'),
    temperature_high_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_alta_extrema'),
    dual_yoyo_alarm: i18n.t('hardcoded.components_alarmprofiles_profileform.dual'),
    dual_yoyo_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.dual_critico'),
    dual_yoyo_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.dual_extremo'),
    thermal_risk_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.riesgo_termico'),
    sensor_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte'),
    receiver_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte'),
};
const BREACH_TYPE_LABELS = { MIN_BREACH: i18n.t('hardcoded.components_alarmprofiles_profileform.minimo'), MAX_BREACH: i18n.t('hardcoded.pages_billingplanspage.maximo') };
const humanizeValue = (v) => v ? String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';
const humanizeRule = (code) => RULE_CODE_LABELS[code] || humanizeValue(code);
const isNoReport = (t) => String(t?.metric_type || '').toUpperCase() === 'NO_REPORT';

// Tiempo transcurrido legible: "Hace 12 min", "Hace 2 h", "Hace 1 d 4 h"
const formatElapsed = (iso) => {
    if (!iso) return '';
    const start = new Date(iso).getTime();
    const now = Date.now();
    let diff = Math.max(0, Math.floor((now - start) / 1000));
    if (diff < 60) return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.hace_unos_segundos');
    const mins = Math.floor(diff / 60);
    if (mins < 60) return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.hace_min', { mins });
    const hours = Math.floor(mins / 60);
    const remMin = mins % 60;
    if (hours < 24) return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.hace_hours', { hours, remMin });
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.hace_days', { days, remH });
};

const formatTicketNumber = (ticket) => {
    if (!ticket) return '';
    if (ticket.ticket_number) return ticket.ticket_number;
    // Fallback consistente con prefijo APS- aunque venga sólo UUID.
    const id = String(ticket.id || ticket.ticket_id || '');
    return id ? `APS-${id.slice(0, 8).toUpperCase()}` : 'APS-?';
};

const formatProblem = (ticket) => {
    const reason = ticket?.alarm_reason;
    if (reason === 'dual_yoyo') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.dual');
    if (reason === 'sensor_no_report') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor_sin_reporte');
    if (reason === 'receiver_no_report') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.receptor_sin_reporte');
    const metric = String(ticket?.metric_type || '').toUpperCase();
    const breach = String(ticket?.breach_type || '').toUpperCase();
    if (metric === 'NO_REPORT') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sin_reporte');
    if (metric === 'PSI' && breach === 'MIN_BREACH') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.psi_bajo');
    if (metric === 'PSI' && breach === 'MAX_BREACH') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.psi_alto');
    if (metric === 'TEMP') return i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura');
    return ticket?.metric_type || i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_label');
};

const STATUS_COLOR = {
    open: 'error',
    acknowledged: 'warning',
    resolved: 'success'
};

const STATUS_LABEL_KEY = {
    open: 'alarmTickets.status.open',
    acknowledged: 'alarmTickets.status.acknowledged',
    resolved: 'alarmTickets.status.resolved'
};

const SEVERITY_LABEL_KEY = {
    alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarmticketsseverityalarm'),
    critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarmticketsseveritycritical'),
    extreme: 'alarmTickets.severity.extreme'
};

const VehicleTicketsDrawer = ({ open, onClose, vehicle, openTickets = [], acknowledgedTickets = [], onTicketsChanged }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();

    const allTickets = [...openTickets, ...acknowledgedTickets];

    // Filtro por estado dentro del sidebar (todos | open | acknowledged).
    const [statusFilter, setStatusFilter] = useState('all');
    // Filtro por POSICION, combinable con el de estado ('all' = todas).
    const [positionFilter, setPositionFilter] = useState('all');

    // Posiciones involucradas en un ticket. En Dual son DOS: el ticket debe
    // aparecer bajo ambas, no solo bajo la que disparo.
    const ticketPositions = useCallback((ticket) => {
        const meta = ticket?.evaluation_metadata || {};
        const group = Array.isArray(meta.group_positions) ? meta.group_positions : [];
        const list = [ticket?.position, ...group]
            .map((p) => Number(p))
            .filter((p) => Number.isFinite(p) && p > 0);
        // Receptor: los tickets sin posicion se agrupan aparte.
        return list.length ? Array.from(new Set(list)) : ['receiver'];
    }, []);

    // Chips disponibles, con su conteo real sobre los tickets del vehiculo.
    const positionChips = useMemo(() => {
        const counts = new Map();
        [...openTickets, ...acknowledgedTickets].forEach((ticket) => {
            ticketPositions(ticket).forEach((p) => counts.set(p, (counts.get(p) || 0) + 1));
        });
        const numeric = [...counts.keys()].filter((k) => k !== 'receiver').sort((a, b) => a - b);
        const chips = numeric.map((p) => ({ key: p, label: `Pos. ${p}`, count: counts.get(p) }));
        if (counts.has('receiver')) {
            chips.push({ key: 'receiver', label: 'Receptor', count: counts.get('receiver') });
        }
        return chips;
    }, [openTickets, acknowledgedTickets, ticketPositions]);

    const matchesPosition = useCallback(
        (ticket) => positionFilter === 'all' || ticketPositions(ticket).includes(positionFilter),
        [positionFilter, ticketPositions],
    );

    const visibleOpenTickets = useMemo(() => openTickets.filter(matchesPosition), [openTickets, matchesPosition]);
    const visibleAcknowledgedTickets = useMemo(
        () => acknowledgedTickets.filter(matchesPosition), [acknowledgedTickets, matchesPosition]);

    // Popup de detalle para confirmar acciones Visto/Resolver, o solo ver el ticket sin navegar.
    const [actionDialog, setActionDialog] = useState({ open: false, ticket: null, action: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [tick, setTick] = useState(0);

    // Detalle COMPLETO cargado vía API cuando se abre el popup — para que muestre lo mismo
    // que la página del ticket (con todos los campos: regla, motivo, rangos, etc.).
    const [dialogDetail, setDialogDetail] = useState(null);
    const [dialogDetailLoading, setDialogDetailLoading] = useState(false);

    useEffect(() => {
        const t = actionDialog.ticket;
        if (!actionDialog.open || !t) { setDialogDetail(null); return; }
        const id = t.id || t.ticket_id;
        if (!id) return;
        let cancelled = false;
        setDialogDetailLoading(true);
        api.alarmTickets.getById(id)
            .then((r) => { if (!cancelled) setDialogDetail(r.data || null); })
            .catch(() => { if (!cancelled) setDialogDetail(null); })
            .finally(() => { if (!cancelled) setDialogDetailLoading(false); });
        return () => { cancelled = true; };
    }, [actionDialog.open, actionDialog.ticket]);

    // Refresca el tiempo transcurrido cada 30s mientras el sidebar esté abierto.
    useEffect(() => {
        if (!open) return;
        const id = setInterval(() => setTick((t) => t + 1), 30000);
        return () => clearInterval(id);
    }, [open]);

    // Clic en el ticket: abre popup con el detalle SIN navegar fuera de la página actual.
    // Conserva el contexto del usuario (estaba mirando el panel, no se pierde el hilo).
    const openDetail = (ticket) => {
        setActionDialog({ open: true, ticket, action: 'view' });
    };

    const handleTicketClick = (ticketId) => {
        if (!ticketId) return;
        const ticket = allTickets.find((tt) => (tt.id || tt.ticket_id) === ticketId);
        if (ticket) openDetail(ticket);
    };

    // Navegación explícita al ticket completo (sí abandona el panel).
    const goToFullTicketPage = (ticketId) => {
        if (!ticketId) return;
        navigate(`/alarm-tickets/${ticketId}`);
        onClose?.();
    };

    const openConfirm = (ticket, action) => setActionDialog({ open: true, ticket, action });
    const closeConfirm = () => setActionDialog({ open: false, ticket: null, action: null });

    const confirmAction = async () => {
        const { ticket, action } = actionDialog;
        if (!ticket || !action) return;
        const id = ticket.id || ticket.ticket_id;
        setActionLoading(true);
        try {
            if (action === 'acknowledge') {
                await api.alarmTickets.acknowledge([id]);
                showSuccess(t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_marcado_como_visto'));
            } else if (action === 'resolve') {
                await api.alarmTickets.resolve(id, t('hardcoded.components_schematics_vehicleticketsdrawer.resuelto_desde_sidebar'));
                showSuccess(t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_resuelto'));
            }
            closeConfirm();
            onTicketsChanged?.();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.components_schematics_vehicleticketsdrawer.no_se_pudo_completar_la'));
        } finally { setActionLoading(false); }
    };

    const handleViewAll = () => {
        navigate('/alarm-tickets');
        onClose?.();
    };

    const formatTicketDate = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('es-MX', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return '';
        }
    };

    const renderTicketItem = (ticket) => {
        const ticketId = ticket.id || ticket.ticket_id;
        const status = String(ticket.status || 'open').toLowerCase();
        const severity = String(ticket.severity || 'alarm').toLowerCase();
        const position = ticket.position;
        const axle = ticket.axle;
        const value = ticket.current_value ?? ticket.original_alarm_value;
        const alarmReason = String(ticket.alarm_reason || '').toLowerCase();
        const isReceiver = alarmReason === 'receiver_no_report';
        const hideLocation = isReceiver || (Number(axle) === 0 && Number(position) === 0);
        const problemText = formatProblem(ticket);
        const profileName = ticket.alarm_profile_name || ticket.profile_name || 'N/A';
        const apsNumber = formatTicketNumber(ticket);
        // tick fuerza re-render para refrescar tiempo transcurrido; usar created_at o problemStart
        const startedAt = ticket.problem_started_at || ticket.created_at;
        const elapsed = formatElapsed(startedAt);

        return (
            <ListItem
                key={ticketId}
                onClick={() => openDetail(ticket)}
                sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: status === 'open' ? 'error.light' : 'warning.light',
                    borderLeft: '3px solid',
                    borderLeftColor: status === 'open' ? 'error.main' : 'warning.main',
                    borderRadius: 1.5, mb: 1, px: 1.5, py: 1,
                    bgcolor: 'background.paper',
                    flexDirection: 'column', alignItems: 'stretch', gap: 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                {/* Línea 1: APS- número + estado + severidad */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={800} sx={{ fontFamily: 'monospace' }}>
                        {apsNumber}
                    </Typography>
                    <Chip size="small" color={STATUS_COLOR[status] || 'default'}
                        label={t(STATUS_LABEL_KEY[status] || STATUS_LABEL_KEY.open)}
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    {SEVERITY_LABEL_KEY[severity] && (
                        <Chip size="small"
                            color={severity === 'extreme' || severity === 'critical' ? 'error' : 'warning'}
                            variant="outlined"
                            label={t(SEVERITY_LABEL_KEY[severity])}
                            sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    <Tooltip title={t('hardcoded.components_schematics_vehicleticketsdrawer.abrir_pagina_completa_del_ticket')}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); goToFullTicketPage(ticketId); }}
                            sx={{ ml: 'auto' }}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Línea 2: Problema + Perfil */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={700}>{problemText}</Typography>
                    {value != null && !alarmReason.includes('no_report') && (
                        <Typography variant="caption" color="text.secondary">
                            · {Number(value).toFixed(1)} {ticket.unit || ''}
                        </Typography>
                    )}
                    <Chip size="small" icon={<ProfileIcon fontSize="small" />} variant="outlined"
                        label={t('hardcoded.components_schematics_vehicleticketsdrawer.perfil_profilename', { profileName: profileName })} sx={{ height: 20, fontSize: '0.65rem' }} />
                </Box>

                {/* Línea 3: Ubicación */}
                {!hideLocation && (position != null || axle != null) && (
                    <Typography variant="caption" color="text.secondary">
                        {axle != null && `${t('vehicleDetail.alarms.axle', 'Eje')} ${axle}`}
                        {axle != null && position != null && ' · '}
                        {position != null && `${t('vehicleDetail.overview.position', 'Posición')} ${position}`}
                    </Typography>
                )}
                {hideLocation && (
                    <Typography variant="caption" color="text.secondary">
                        {t('vehicleDetail.alarms.receiver', 'Receptor')}
                    </Typography>
                )}

                {/* Línea 4: Fecha + tiempo transcurrido */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {ticket.created_at && (
                        <Typography variant="caption" color="text.secondary">
                            {formatTicketDate(ticket.created_at)}
                        </Typography>
                    )}
                    {elapsed && (
                        <Typography variant="caption" color={status === 'open' ? 'error.main' : 'warning.dark'} fontWeight={600}>
                            <AccessTimeIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                            {elapsed}
                        </Typography>
                    )}
                </Box>

                {/* Acciones rápidas — abren popup de confirmación con detalle */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {status === 'open' && (
                        <Button size="small" variant="outlined" color="warning" startIcon={<VisibilityIcon fontSize="small" />}
                            onClick={(e) => { e.stopPropagation(); openConfirm(ticket, 'acknowledge'); }}>
                            {i18n.t('alarmTickets.status.acknowledged')}</Button>
                    )}
                    <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon fontSize="small" />}
                        onClick={(e) => { e.stopPropagation(); openConfirm(ticket, 'resolve'); }}>
                        {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver')}</Button>
                </Box>
            </ListItem>
        );
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            // Render above everything (including the fixed navbar). On mobile
            // the navbar would otherwise cover the close button at the top.
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 10
            }}
            ModalProps={{
                keepMounted: false,
                // Tap outside / hardware back closes it on mobile
                hideBackdrop: false,
            }}
            PaperProps={{
                sx: {
                    width: isMobile ? '100%' : 420,
                    maxWidth: '100vw',
                    // Make sure the paper is also above the navbar on its own
                    zIndex: (theme) => theme.zIndex.modal + 11
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <TicketIcon color="primary" />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} noWrap>
                                {t('vehicleTicketsDrawer.title', 'Tickets del vehículo')}
                            </Typography>
                            {vehicle && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                    {vehicle.alias || vehicle.display_name || vehicle.name || ''}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        aria-label={t('common.close')}
                        size="large"
                        sx={{
                            ml: 1,
                            flexShrink: 0,
                            // Ensure the close button is always tappable on mobile
                            minWidth: 44,
                            minHeight: 44
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Filtros: estado (fila 1) y posicion (fila 2). Se combinan. */}
                <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Chip
                        size="small"
                        color={statusFilter === 'all' ? 'primary' : 'default'}
                        variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                        label={t('hardcoded.components_schematics_vehicleticketsdrawer.todos_openticketsleng', { openTicketsleng: openTickets.length + acknowledgedTickets.length })}
                        onClick={() => setStatusFilter('all')}
                        clickable
                    />
                    <Chip
                        size="small"
                        color={statusFilter === 'open' ? 'error' : 'default'}
                        variant={statusFilter === 'open' ? 'filled' : 'outlined'}
                        label={`${openTickets.length} ${t('vehicleTicketsDrawer.openLabel', 'abiertos')}`}
                        onClick={() => setStatusFilter('open')}
                        clickable
                    />
                    <Chip
                        size="small"
                        color={statusFilter === 'acknowledged' ? 'warning' : 'default'}
                        variant={statusFilter === 'acknowledged' ? 'filled' : 'outlined'}
                        label={`${acknowledgedTickets.length} ${t('vehicleTicketsDrawer.acknowledgedLabel', 'vistos')}`}
                        onClick={() => setStatusFilter('acknowledged')}
                        clickable
                    />

                    {/* Por posicion. Un ticket Dual aparece bajo SUS DOS posiciones. */}
                    {positionChips.length > 1 && (
                        <>
                            <Box sx={{ flexBasis: '100%', height: 0 }} />
                            <Chip
                                size="small"
                                color={positionFilter === 'all' ? 'primary' : 'default'}
                                variant={positionFilter === 'all' ? 'filled' : 'outlined'}
                                label={t('hardcoded.components_alarmprofiles_assignmentstable.todos')}
                                onClick={() => setPositionFilter('all')}
                                clickable
                            />
                            {positionChips.map((chip) => (
                                <Chip
                                    key={chip.key}
                                    size="small"
                                    color={positionFilter === chip.key ? 'primary' : 'default'}
                                    variant={positionFilter === chip.key ? 'filled' : 'outlined'}
                                    label={`${chip.label} (${chip.count})`}
                                    onClick={() => setPositionFilter(chip.key)}
                                    clickable
                                />
                            ))}
                        </>
                    )}
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                    {allTickets.length === 0 ? (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {t('vehicleTicketsDrawer.empty', 'No hay tickets activos para este vehículo.')}
                        </Alert>
                    ) : (
                        <Stack spacing={0}>
                            {(statusFilter === 'all' || statusFilter === 'open') && visibleOpenTickets.length > 0 && (
                                <>
                                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {t('vehicleTicketsDrawer.openSection', 'Abiertos')}
                                    </Typography>
                                    <List dense disablePadding>
                                        {visibleOpenTickets.map(renderTicketItem)}
                                    </List>
                                </>
                            )}
                            {(statusFilter === 'all' || statusFilter === 'acknowledged') && visibleAcknowledgedTickets.length > 0 && (
                                <>
                                    {statusFilter === 'all' && visibleOpenTickets.length > 0 && <Divider sx={{ my: 1.5 }} />}
                                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {t('vehicleTicketsDrawer.acknowledgedSection', 'Vistos')}
                                    </Typography>
                                    <List dense disablePadding>
                                        {visibleAcknowledgedTickets.map(renderTicketItem)}
                                    </List>
                                </>
                            )}
                            {/* Empty state contextual al filtro */}
                            {statusFilter === 'open' && visibleOpenTickets.length === 0 && (
                                <Alert severity="info" sx={{ mt: 1 }}>{t('hardcoded.components_schematics_vehicleticketsdrawer.no_hay_tickets_abiertos')}</Alert>
                            )}
                            {statusFilter === 'acknowledged' && visibleAcknowledgedTickets.length === 0 && (
                                <Alert severity="info" sx={{ mt: 1 }}>{t('hardcoded.components_schematics_vehicleticketsdrawer.no_hay_tickets_vistos')}</Alert>
                            )}
                        </Stack>
                    )}
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleViewAll}
                        startIcon={<TicketIcon />}
                    >
                        {t('vehicleTicketsDrawer.viewAll', 'Ver todos los tickets')}
                    </Button>
                </Box>
            </Box>

            {/* Popup con detalle del ticket — sirve para VER el ticket sin salir de la página
                ('view') o para CONFIRMAR una acción ('acknowledge'/'resolve').
                IMPORTANTE: el Drawer está en modal+10/11. Este Dialog debe ir más arriba (modal+30)
                para que aparezca ENCIMA del sidebar y no detrás. */}
            <Dialog
                open={actionDialog.open}
                onClose={closeConfirm}
                maxWidth="md"
                fullWidth
                sx={{ zIndex: (th) => th.zIndex.modal + 30 }}
                PaperProps={{ sx: { zIndex: (th) => th.zIndex.modal + 31 } }}
            >
                <DialogTitle>
                    {actionDialog.action === 'acknowledge' ? t('hardcoded.components_schematics_alarmmanager.marcar_como_visto')
                        : actionDialog.action === 'resolve' ? t('hardcoded.components_schematics_vehicleticketsdrawer.resolver_ticket')
                        : t('hardcoded.components_schematics_vehicleticketsdrawer.detalle_del_ticket')}
                </DialogTitle>
                <DialogContent dividers>
                    {actionDialog.ticket && (() => {
                        // Preferir el detalle COMPLETO (cargado por API) y caer al ticket parcial del listado.
                        const full = dialogDetail || {};
                        const t_ticket = { ...actionDialog.ticket, ...full };
                        const t_status = String(t_ticket.status || 'open').toLowerCase();
                        const unit = t_ticket.unit || '';
                        const _isNoReport = isNoReport(t_ticket);

                        const generalFields = [
                            { label: t('vehicles.table.vehicle'), value: t_ticket.vehicle_name || vehicle?.alias || vehicle?.display_name || '—' },
                            t_ticket.position != null && { label: t('alarms.list.position'), value: t('hardcoded.components_schematics_vehicleticketsdrawer.posicion_position_eje_t_ticketaxle', { position: t_ticket.position, t_ticketaxle: t_ticket.axle ?? '—' }) },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.sensor'), value: t_ticket.sensor_id || 'N/A' },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.perfil_de_alerta'), value: t_ticket.alarm_profile_name || t_ticket.profile_name || t('hardcoded.components_schematics_vehicleticketsdrawer.sin_perfil') },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.motivo'), value: ALARM_REASON_LABELS[t_ticket.alarm_reason] || humanizeValue(t_ticket.alarm_reason) },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.aps_severidad'), value: `${t_ticket.aps_level || 'N/A'} · ${SEVERITY_LABELS[t_ticket.severity] || humanizeValue(t_ticket.severity)}` },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.regla_aplicada'), value: humanizeRule(t_ticket.rule_code) },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.tipo_de_ticket'), value: t_ticket.is_subticket ? 'Subticket' : t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_padre') },
                            t_ticket.created_at && { label: t('gps.card.created'), value: formatTicketDate(t_ticket.created_at) },
                            { label: t('hardcoded.components_schematics_vehicleticketsdrawer.tiempo_activo'), value: formatElapsed(t_ticket.problem_started_at || t_ticket.created_at) },
                        ].filter(Boolean);

                        const valueFields = [];
                        if (_isNoReport) {
                            valueFields.push({ label: t('hardcoded.components_schematics_vehicleticketsdrawer.tiempo_sin_reportar'), value: `${Number(t_ticket.current_value ?? 0).toFixed(1)} h`, highlight: true });
                            valueFields.push({
                                label: t('hardcoded.components_schematics_vehicleticketsdrawer.ultimo_reporte'),
                                value: t_ticket.evaluation_metadata?.last_seen_at
                                    ? new Date(t_ticket.evaluation_metadata.last_seen_at).toLocaleString('es-MX')
                                    : '—',
                            });
                            valueFields.push({
                                label: t('vehicles.form.type'),
                                value: t_ticket.alarm_reason === 'sensor_no_report'
                                    ? t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte')
                                    : t_ticket.alarm_reason === 'receiver_no_report'
                                        ? t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte')
                                        : t('hardcoded.components_schematics_vehicleticketsdrawer.sin_reporte'),
                            });
                        } else if (t_ticket.current_value != null || t_ticket.original_value != null) {
                            valueFields.push({
                                label: t('hardcoded.components_alarms_alarmlist.valor_actual'),
                                value: t_ticket.current_value != null ? `${Number(t_ticket.current_value).toFixed(2)} ${unit}` : 'N/A',
                                highlight: true,
                            });
                            if (t_ticket.original_value != null) {
                                valueFields.push({ label: t('hardcoded.components_schematics_vehicleticketsdrawer.valor_original'), value: `${Number(t_ticket.original_value).toFixed(2)} ${unit}` });
                            }
                            const isMin = t_ticket.breach_type === 'MIN_BREACH';
                            valueFields.push({
                                label: isMin ? t('hardcoded.components_alarms_alarmform.umbral_minimo') : t('hardcoded.components_alarms_alarmform.umbral_maximo'),
                                value: isMin
                                    ? `${t_ticket.threshold_min ?? '—'} ${unit}`
                                    : `${t_ticket.threshold_max ?? '—'} ${unit}`,
                            });
                            valueFields.push({
                                label: t('hardcoded.components_schematics_vehicleticketsdrawer.tipo_de_violacion'),
                                value: BREACH_TYPE_LABELS[t_ticket.breach_type] || t_ticket.breach_type || '—',
                            });
                            if (t_ticket.resolution_range_min != null || t_ticket.resolution_range_max != null) {
                                valueFields.push({
                                    label: t('hardcoded.components_schematics_vehicleticketsdrawer.rango_de_cierre'),
                                    value: `${t_ticket.resolution_range_min ?? '...'} a ${t_ticket.resolution_range_max ?? '...'} ${unit}`,
                                });
                            }
                        }

                        const renderField = (field, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
                                    {field.label}
                                </Typography>
                                <Typography
                                    variant={field.highlight ? 'h6' : 'body2'}
                                    sx={{
                                        wordBreak: 'break-word',
                                        fontWeight: field.highlight ? 700 : 400,
                                        color: field.highlight ? 'error.main' : 'text.primary',
                                    }}
                                >
                                    {field.value}
                                </Typography>
                            </Grid>
                        );

                        return (
                            <Stack spacing={2}>
                                {/* Encabezado */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="h6" fontWeight={800} sx={{ fontFamily: 'monospace' }}>
                                        {formatTicketNumber(t_ticket)}
                                    </Typography>
                                    <Chip size="small" color={STATUS_COLOR[t_status] || 'default'}
                                        label={t(STATUS_LABEL_KEY[t_status] || STATUS_LABEL_KEY.open)} />
                                    <Chip size="small" variant="outlined" color="error" label={SEVERITY_LABELS[t_ticket.severity] || humanizeValue(t_ticket.severity)} />
                                    {dialogDetailLoading && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                                </Box>

                                {/* Información General */}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        {t('hardcoded.components_schematics_vehicleticketsdrawer.informacion_general')}</Typography>
                                    <Grid container spacing={1.5}>
                                        {generalFields.map(renderField)}
                                    </Grid>
                                </Box>

                                {/* Valores y Umbrales */}
                                {valueFields.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.valores_y_umbrales')}</Typography>
                                        <Grid container spacing={1.5}>
                                            {valueFields.map(renderField)}
                                        </Grid>
                                    </Box>
                                )}

                                {t_ticket.reason_text && (
                                    <Alert severity="info">{t_ticket.reason_text}</Alert>
                                )}

                                {actionDialog.action === 'acknowledge' && (
                                    <Alert severity="warning">
                                        {t('hardcoded.components_schematics_vehicleticketsdrawer.marcaras_este_ticket_como_visto')}</Alert>
                                )}
                                {actionDialog.action === 'resolve' && (
                                    <Alert severity="success">
                                        {t('hardcoded.components_schematics_vehicleticketsdrawer.marcaras_este_ticket_como_resuelto')}</Alert>
                                )}

                                {/* Analisis + accesos directos, para decidir sin salir del popup. */}
                                <TicketQuickActions ticket={t_ticket} onClose={closeConfirm} />

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        size="small"
                                        startIcon={<OpenInNewIcon fontSize="small" />}
                                        onClick={() => goToFullTicketPage(t_ticket.id || t_ticket.ticket_id)}
                                    >
                                        {t('hardcoded.components_schematics_vehicleticketsdrawer.abrir_pagina_completa_del_ticket')}</Button>
                                </Box>
                            </Stack>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm} disabled={actionLoading}>{t('common.close')}</Button>
                    {actionDialog.action === 'view' && actionDialog.ticket && (
                        <>
                            {String(actionDialog.ticket.status || '').toLowerCase() === 'open' && (
                                <Button
                                    variant="outlined" color="warning"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => setActionDialog((p) => ({ ...p, action: 'acknowledge' }))}
                                    disabled={actionLoading}
                                >
                                    {t('hardcoded.components_schematics_alarmmanager.marcar_como_visto')}</Button>
                            )}
                            <Button
                                variant="contained" color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => setActionDialog((p) => ({ ...p, action: 'resolve' }))}
                                disabled={actionLoading}
                            >
                                {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver')}</Button>
                        </>
                    )}
                    {actionDialog.action !== 'view' && (
                        <Button
                            variant="contained"
                            color={actionDialog.action === 'acknowledge' ? 'warning' : 'success'}
                            disabled={actionLoading}
                            startIcon={actionLoading ? <CircularProgress size={14} /> :
                                (actionDialog.action === 'acknowledge' ? <VisibilityIcon /> : <CheckCircleIcon />)}
                            onClick={confirmAction}
                        >
                            {actionDialog.action === 'acknowledge' ? t('hardcoded.components_schematics_vehicleticketsdrawer.confirmar_visto') : t('hardcoded.components_schematics_vehicleticketsdrawer.confirmar_resolucion')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default VehicleTicketsDrawer;
